/**
 * SignUp.jsx - New User Registration (OTP pre-verified)
 *
 * CHANGES:
 *   - Email made OPTIONAL (clearly labelled)
 *   - Password removed (OTP auth)
 *   - "Already have account" button removed
 *   - Mobile pre-filled and locked from OTP verification step
 */

import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authenticate, triggerRegister } from "../../Auth/helper";
import Header from "./Header";
import { FaUser, FaEnvelope, FaMobileAlt, FaCheckCircle } from "react-icons/fa";
import LoadingSpinner from "../../components/LoadingSpinner";

const inputStyle = {
  width: "100%", padding: "12px 40px 12px 40px",
  borderRadius: 12, border: "1px solid #e0e0e0",
  fontSize: 16, marginBottom: 8, background: "#fafbfc",
  boxSizing: "border-box",
};
const lockedInputStyle = {
  ...inputStyle, background: "#f0fdf4",
  border: "1.5px solid #4caf50", color: "#388e3c", fontWeight: 600,
};
const iconStyle = { position: "absolute", left: 16, top: 14, color: "#bdbdbd" };
const checkStyle = { position: "absolute", right: 16, top: 14, color: "#4caf50" };
const cardStyle = {
  width: "90%", maxWidth: 500, margin: "20px auto",
  background: "#fff", borderRadius: 18,
  boxShadow: "0 4px 32px #e0e0e0", padding: "24px", position: "relative",
};
const buttonStyle = {
  width: "100%", padding: "14px 0", borderRadius: 8,
  background: "#E34607", color: "#fff", fontWeight: 600,
  fontSize: 18, border: "none", marginTop: 16, marginBottom: 8,
  display: "flex", alignItems: "center", justifyContent: "center",
  gap: 8, cursor: "pointer",
};

// Email format validator
const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).toLowerCase());
};

const SignUp = () => {
  const Navigate = useNavigate();
  const location = useLocation();

  // ALL hooks first — before any early return
  const [formValue, setFormValue] = useState({
    Name: "", EmailId: "", Address: "", errorMessage: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  const verifiedMobile = location.state?.MobileNo || "";
  const otpVerified = location.state?.otpVerified || false;
  const { Name, EmailId, Address, errorMessage } = formValue;

  // Guard: redirect if reached without OTP verification
  if (!otpVerified || !verifiedMobile) {
    Navigate("/signin");
    return null;
  }

  const handleChange = (name) => (e) => {
    setFormValue({ ...formValue, [name]: e.target.value, errorMessage: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormValue({ ...formValue, errorMessage: false });

    // Only Name and Address required — Email is optional
    if (!Name || !Address) {
      setFormValue({ ...formValue, errorMessage: "Please fill in your Name and Address." });
      return;
    }

    // If email provided, validate format
    if (EmailId && !isValidEmail(EmailId)) {
      setFormValue({ ...formValue, errorMessage: "Please enter a valid email address (e.g. name@example.com)." });
      return;
    }

    setIsLoading(true);
    try {
      const result = await triggerRegister({
        Name, EmailId: EmailId || "", Address, MobileNo: verifiedMobile,
      });

      if (result?.error) {
        setFormValue({ ...formValue, errorMessage: result.error });
        return;
      }

      authenticate(result);
      Navigate("/");
    } catch {
      setFormValue({ ...formValue, errorMessage: "An error occurred. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center",
        justifyContent: "center", background: "#fafbfc",
        padding: "20px", boxSizing: "border-box",
      }}>
        <div style={cardStyle}>
          <h2 style={{ fontWeight: 700, fontSize: "28px", marginBottom: 4 }}>
            Complete Registration
          </h2>

          {/* OTP verified badge */}
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            color: "#4caf50", fontWeight: 600, fontSize: 14, marginBottom: 20,
          }}>
            <FaCheckCircle />
            One Time OTP Verified — Registration required only once
          </div>

          <form autoComplete="off" onSubmit={handleSubmit}>

            {/* Mobile — pre-filled, locked */}
            <div style={{ position: "relative", marginBottom: 18 }}>
              <FaMobileAlt style={{ ...iconStyle, color: "#4caf50" }} />
              <input type="text" value={verifiedMobile} readOnly style={lockedInputStyle} />
              <span style={checkStyle}>&#10003;</span>
            </div>

            {/* Full Name — required */}
            <div style={{ position: "relative", marginBottom: 18 }}>
              <FaUser style={iconStyle} />
              <input
                type="text" name="Name" onChange={handleChange("Name")}
                placeholder="Full Name *"
                style={inputStyle} value={Name} autoComplete="off"
              />
              {Name && <span style={checkStyle}>&#10003;</span>}
            </div>

            {/* Email — optional */}
            <div style={{ position: "relative", marginBottom: EmailId && !isValidEmail(EmailId) ? 4 : 18 }}>
              <FaEnvelope style={iconStyle} />
              <input
                type="email" name="EmailId" onChange={handleChange("EmailId")}
                placeholder="Email Address (Optional)"
                style={{
                  ...inputStyle,
                  border: EmailId && !isValidEmail(EmailId) ? "1.5px solid #f44336" : "1px solid #e0e0e0",
                }}
                value={EmailId} autoComplete="off"
              />
              {/* Optional badge */}
              {!EmailId && (
                <span style={{
                  position: "absolute", right: 14, top: 14,
                  fontSize: 11, color: "#bdbdbd", fontWeight: 500,
                  background: "#f5f5f5", padding: "2px 6px", borderRadius: 4,
                }}>
                  Optional
                </span>
              )}
              {EmailId && isValidEmail(EmailId) && <span style={checkStyle}>&#10003;</span>}
              {EmailId && !isValidEmail(EmailId) && (
                <span style={{ position: "absolute", right: 14, top: 14, color: "#f44336" }}>&#10007;</span>
              )}
            </div>
            {EmailId && !isValidEmail(EmailId) && (
              <div style={{ color: "#f44336", fontSize: 12, marginBottom: 12, paddingLeft: 4 }}>
                Please enter a valid email address (e.g. name@example.com)
              </div>
            )}

            {/* Address — required */}
            <div style={{ position: "relative", marginBottom: 18 }}>
              <FaUser style={iconStyle} />
              <input
                type="text" name="Address" onChange={handleChange("Address")}
                placeholder="Address *"
                style={inputStyle} value={Address} autoComplete="off"
              />
              {Address && <span style={checkStyle}>&#10003;</span>}
            </div>

            {/* Required fields note */}
            <p style={{ fontSize: 12, color: "#bdbdbd", marginBottom: 8 }}>
              * Required fields
            </p>

            <button type="submit" style={buttonStyle} disabled={isLoading}>
              {isLoading
                ? <LoadingSpinner size="1x" color="#fff" text="Creating Account..." showText={true} />
                : <>Create Account & Sign In <span style={{ fontSize: 20, marginLeft: 8 }}>&#8594;</span></>}
            </button>
          </form>

          {errorMessage && (
            <div className="alert alert-warning fade show" role="alert" style={{ marginTop: 12 }}>
              <strong>{errorMessage}</strong>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SignUp;